import {
  Icon,
  IPanelStyles,
  mergeStyles,
  MessageBar,
  MessageBarType,
  Panel,
  PanelType,
  Pivot,
  PivotItem,
  PrimaryButton,
  SearchBox,
  Spinner,
  SpinnerSize,
  Stack,
  Text,
  TooltipHost,
} from '@fluentui/react';
import * as React from 'react';
import styled from 'styled-components';

import { SubscriptionNode } from '../../../../Common/ConsumerModels/SubcriptionNode';
import { useGetApprovedSubscriptionAttributes } from '../../../../Hooks/Consumer/useGetApprovedSubscriptionAttributes';

type ViewApprovedAttributesPanelProps = {
  isOpen: boolean;
  dismissPanel(): void;
  subscriberId: string;
};

const stickyFooterPanelStyles: Partial<IPanelStyles> = {
  footer: {
    borderTop: '1px solid #e5e5e5',
  },
  content: {
    overflowX: 'hidden',
  },
};

export function ViewApprovedAttributesPanel(props: ViewApprovedAttributesPanelProps): React.ReactElement {
  const { isOpen, dismissPanel, subscriberId } = props;
  const [selectedEnvironmentPivotKey, setSelectedEnvironmentPivotKey] = React.useState('0');
  const [searchQuery, setSearchQuery] = React.useState<string>('');
  const [
    formattedApprovedAttributes,
    setFormattedApprovedAttributes,
    filteredApprovedAttributes,
    setFilteredApprovedAttributes,
    isApprovedAttributesLoading,
  ] = useGetApprovedSubscriptionAttributes(Number(selectedEnvironmentPivotKey), subscriberId, searchQuery);

  const dropdownIconClass = mergeStyles({
    fontSize: 20,
    height: 20,
    width: 20,
    color: '#242424',
  });

  const completedCheckIconClass = mergeStyles({
    fontSize: 16,
    height: 16,
    width: 16,
    color: '#0078D4',
    margin: '2px 0px',
  });

  const lockIconClass = mergeStyles({
    fontSize: 14,
    height: 14,
    width: 14,
    color: '#605E5C',
    marginLeft: 8,
  });

  const handlePivotChange = (item?: PivotItem) => {
    if (item) {
      setSelectedEnvironmentPivotKey(item.props.itemKey);
    }
  };

  const onChangeSearchBoxQuery = (newSearchQuery: string) => {
    setSearchQuery(newSearchQuery);
  };

  const onChangeSearchBoxClear = () => {
    setSearchQuery('');
  };

  const onChangeDropdown = (tenantNode: SubscriptionNode, filteredTenantIndex: number): void => {
    // Set dropdown for formattedApprovedAttributes tree
    const newFormattedApprovedAttributes: SubscriptionNode[] = JSON.parse(
      JSON.stringify(formattedApprovedAttributes) // deep copy
    );
    const formattedTenantIndex: number = tenantNode.pathFromRoot as unknown as number;
    const formattedDropdown: boolean = newFormattedApprovedAttributes[formattedTenantIndex].isDropdownExpand;
    newFormattedApprovedAttributes[formattedTenantIndex].isDropdownExpand = !formattedDropdown;
    setFormattedApprovedAttributes(newFormattedApprovedAttributes);
    // Set dropdown for filteredAprovedAttributes tree
    const newFilteredApprovedAttributes: SubscriptionNode[] = JSON.parse(
      JSON.stringify(formattedApprovedAttributes) // deep copy
    );
    const filteredDropdown: boolean = newFilteredApprovedAttributes[filteredTenantIndex].isDropdownExpand;
    newFilteredApprovedAttributes[filteredTenantIndex].isDropdownExpand = !filteredDropdown;
    setFilteredApprovedAttributes(newFilteredApprovedAttributes);
  };

  const getEgressOptionsFromAttributeNode = (attributeNode: SubscriptionNode): string => {
    const egressOptionsSet: Set<string> = new Set();

    // Push all egress options into a set to remove duplicate values
    attributeNode.children.forEach((egressNode: SubscriptionNode) => {
      if (!egressOptionsSet.has(egressNode.name)) {
        egressOptionsSet.add(egressNode.name);
      }
    });

    // Convert the set back into an array
    const egressOptionsArray: string[] = Array.from(egressOptionsSet);
    
    // Format the egress options text string depending on the available egress options
    let egressOptionsText = '(';
    egressOptionsArray.forEach((egressOptionName: string, index: number) => {
      egressOptionsText = egressOptionsText.concat(egressOptionName as string);
      if (egressOptionsArray.length - 1 === index) {
        egressOptionsText = egressOptionsText.concat(')');
      } else {
        egressOptionsText = egressOptionsText.concat(', ');
      }
    });
    return egressOptionsText;
  };

  const renderTenantTitleDropdown = (tenantNode: SubscriptionNode, filteredTenantIndex: number): JSX.Element => {
    return (
      <SingleTenantContainer>
        <TenantTextIconContainer>
          <TenantTextStyled>{tenantNode.name}</TenantTextStyled>
          {tenantNode.isDropdownExpand && (
            <Icon
              aria-label="Contract Attribute Subscriptions"
              iconName="ChevronUp"
              className={dropdownIconClass}
              onClick={() => onChangeDropdown(tenantNode, filteredTenantIndex)}
            />
          )}
          {!tenantNode.isDropdownExpand && (
            <Icon
              aria-label="Expand Attribute Subscriptions"
              iconName="ChevronDown"
              className={dropdownIconClass}
              onClick={() => onChangeDropdown(tenantNode, filteredTenantIndex)}
            />
          )}
        </TenantTextIconContainer>
        <TenantDivider />
      </SingleTenantContainer>
    );
  };

  const renderApprovedAttribute = (attributeNode: SubscriptionNode): JSX.Element => {
    const egressOptionsText: string = getEgressOptionsFromAttributeNode(attributeNode);
    return (
      <Stack horizontal tokens={{ childrenGap: 16 }}>
        <Icon aria-label="Attribute Icon" iconName="Completed" className={completedCheckIconClass} />
        <Stack horizontal tokens={{ childrenGap: 4 }}>
          <Text>{attributeNode.name}</Text>
          <EgressOptionsTextStyled>{egressOptionsText}</EgressOptionsTextStyled>
        </Stack>
      </Stack>
    );
  };

  const renderCdmNode = (cdmNode: SubscriptionNode): JSX.Element => {
    return (
      <CdmTextContainer>
        <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
          <CdmTextStyled>{cdmNode.name}</CdmTextStyled>
            {cdmNode.isEO14117Protected && (
            <Stack horizontal tokens={{ childrenGap: 4 }} verticalAlign="center">
              <TooltipHost content="This CDM is protected under Executive Order 14117">
              <Icon
                aria-label="EO14117 Protected"
                iconName="Lock"
                className={lockIconClass}
              />
              </TooltipHost>
              <EOProtectedTextStyled>(EO14117 Protected)</EOProtectedTextStyled>
            </Stack>
            )}
        </Stack>
      </CdmTextContainer>
    );
  };

  const onRenderFooterContent = () => {
    return <PrimaryButton text="Close" onClick={dismissPanel} />;
  };

  const onRenderHeader = () => {
    return (
      <PanelHeaderContainer>

        <PanelHeaderTitleText>Attribute Subscriptions</PanelHeaderTitleText>
        <PanelHeaderDescriptionText>List of approved attribute subscriptions</PanelHeaderDescriptionText>
        
        <PivotStyled
          aria-label="Non-Production and Production Pivot Tabs"
          selectedKey={selectedEnvironmentPivotKey}
          onLinkClick={handlePivotChange}
          defaultSelectedKey="Non-Production"
        >
          <PivotItem headerText="Non-Production" itemKey="0" />
          <PivotItem headerText="Production" itemKey="1" />
        </PivotStyled>

        <HeaderDivider />

        <SearchBoxContainer>
          <SearchBox
            placeholder="Search and Press Enter"
            aria-label="Search for Attribute Subscriptions"
            defaultValue={searchQuery}
            onSearch={(newSearchQuery) => onChangeSearchBoxQuery(newSearchQuery)}
            onClear={onChangeSearchBoxClear}
          />
        </SearchBoxContainer>

      </PanelHeaderContainer>
    );
  };

  return (
    <Panel
      isOpen={isOpen}
      type={PanelType.medium}
      onDismiss={dismissPanel}
      onRenderHeader={onRenderHeader}
      onRenderFooterContent={onRenderFooterContent}
      hasCloseButton={false}
      isFooterAtBottom={true}
      isLightDismiss={true}
      styles={stickyFooterPanelStyles}
    >
      <AttributeSubscriptionsContainer>
        {!isApprovedAttributesLoading &&
          filteredApprovedAttributes?.map((tenantNode: SubscriptionNode, filteredTenantIndex: number) => (
            <div>
              {renderTenantTitleDropdown(tenantNode, filteredTenantIndex)}
              {tenantNode.isDropdownExpand && (
                <MultipleCdmContainer>
                  {tenantNode.children?.map((cdmNode: SubscriptionNode, cdmIndex: number) => (
                    <div key={`cdm-${cdmIndex}-${cdmNode.name}`}>
                      {renderCdmNode(cdmNode)}
                      <MultipleAttributeContainer>
                        {cdmNode.children?.map((attributeNode: SubscriptionNode, attrIndex: number) => (
                          <div key={`attr-${attrIndex}-${attributeNode.name}`}>
                            {renderApprovedAttribute(attributeNode)}
                          </div>
                        ))}
                      </MultipleAttributeContainer>
                    </div>
                  ))}
                </MultipleCdmContainer>
              )}
            </div>
          ))}

        {isApprovedAttributesLoading && <Spinner size={SpinnerSize.large} />}

        {!isApprovedAttributesLoading && filteredApprovedAttributes.length === 0 && (
          <MessageBar messageBarType={MessageBarType.info}>
            Approved Attributes not found for this Environment
          </MessageBar>
        )}
      </AttributeSubscriptionsContainer>
    </Panel>
  );
}

const PanelHeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const PanelHeaderTitleText = styled(Text)`
  font-weight: 700;
  font-size: 26px;
  line-height: 32px;
  color: #171717;
  padding-top: 32px;
  padding-bottom: 8px; // 16px
  padding-left: 48px;
  padding-right: 48px;
`;

const PanelHeaderDescriptionText = styled(Text)`
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #646464;
  padding-top: 0px;
  padding-bottom: 16px;
  padding-left: 48px;
  padding-right: 48px;
`;

const PivotStyled = styled(Pivot)`
  display: flex;
  margin-left: 40px;
`;

const HeaderDivider = styled.div`
  display: flex;
  border-bottom: 1px solid #e5e5e5;
  margin-top: -6px;
`;

const SearchBoxContainer = styled.div`
  padding-top: 32px;
  padding-bottom: 32px;
  padding-left: 48px;
  padding-right: 48px;
  border-bottom: 1px solid #e5e5e5;
`;

const AttributeSubscriptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 32px;
  padding-top: 32px;
  padding-bottom: 16px;
  padding-left: 24px;
  padding-right: 24px;
`;

const SingleTenantContainer = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 12px;
  align-items: space-between;
`;

const TenantTextIconContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: 548px;
`;

const TenantDivider = styled.div`
  border: 1px solid #e5e5e5;
  width: 548px;
`;

const TenantTextStyled = styled(Text)`
  font-weight: 600;
  font-size: 18px;
  line-height: 24px;
  color: #323130;
`;

const MultipleCdmContainer = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 32px;
  padding-top: 32px;
  padding-bottom: 32px;
`;

const CdmTextContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding-bottom: 24px;
`;

const CdmTextStyled = styled(Text)`
  font-weight: 600;
  font-size: 14px;
  line-height: 14px;
  box-shadow: #171717;
`;

const MultipleAttributeContainer = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 16px;
`;

const EgressOptionsTextStyled = styled(Text)`
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #646464;
`;

const EOProtectedTextStyled = styled(Text)`
  font-weight: 400;
  font-size: 12px;
  line-height: 14px;
  color: #605E5C;
  font-style: italic;
`;
